import {useEffect} from 'react'
import {useQueryClient} from '@tanstack/react-query'
import {socket} from '@/socket'
import {QUERY_KEYS} from '@/queryClient'
import {NotificationCount, SocketNotification, UserNotification} from '../types'
import {addNotificationToCache} from '../utils/cacheUtils'

export const useSocketNotifications = (userId: string) => {
    const queryClient = useQueryClient()

    useEffect(() => {
        if (!userId) return
        // Join the user-specific room
        socket.emit('join', `users:${userId}`)

        const intervalId = setInterval(() => {
            socket.emit('ping')
        }, 60000)

        socket.once('pong', (data: {sessionEnabled: boolean}) => {
            if (!data.sessionEnabled) {
                console.log('Socket session disabled:', data)
            }
        })

        // Handle incoming notifications
        socket.on('notification', (rawSocketData: unknown) => {
            try {
                // Validate the incoming notification data
                const parseResult = SocketNotification.safeParse(rawSocketData)

                if (!parseResult.success) {
                    // `type` is deliberately unvalidated (see NOTIFICATION_TYPE), so a
                    // notification of a type the frontend does not know still arrives.
                    // Reaching here means the payload is genuinely malformed — a missing
                    // or wrongly-shaped required field — not merely an unknown type.
                    console.error('Malformed notification payload received (not an unknown type):', parseResult.error)
                    return
                }

                const socketData = parseResult.data

                // Get content from either bodyTemplate or body field
                const content = socketData.bodyTemplate || socketData.body || ''

                // Create a UserNotification object to match the format used in the queries
                const userNotification: UserNotification = {
                    id: socketData.id,
                    userId: userId,
                    notificationId: socketData.id,
                    conversationId: socketData.conversationId ?? null,
                    readAt: null,
                    details: null,
                    notification: {
                        id: socketData.id,
                        title: socketData.title,
                        bodyTemplate: content,
                        details: null,
                        type: socketData.type,
                        severity: socketData.severity,
                        link: socketData.link,
                        triggeredBy: socketData.triggeredBy || 'system',
                        createdAt: socketData.createdAt,
                        updatedAt: socketData.createdAt,
                        scheduledAt: null,
                        sentAt: socketData.sentAt || socketData.createdAt,
                        extraData: socketData.extraData || null,
                        // The socket payload only carries the triggering user's id, not
                        // their name — leave it blank so NotificationItem knows to resolve
                        // it via useGetUser instead of showing a fake "System" sender.
                        triggeredByUser: {
                            id: socketData.triggeredBy || 'system',
                            firstName: '',
                            lastName: ''
                        }
                    }
                }

                // Add the new notification to all cached queries first — if this exact
                // notification (same id) was already in the list, e.g. a duplicate
                // socket delivery, the total count must not be bumped for it.
                const {hadExistingEntry} = addNotificationToCache(queryClient, userNotification)

                // Optimistically update notification count
                queryClient.setQueryData<NotificationCount>([QUERY_KEYS.NOTIFICATION_COUNT], oldData => {
                    if (!oldData) return {toRead: 1, total: hadExistingEntry ? 0 : 1}
                    return {
                        toRead: oldData.toRead + 1,
                        total: oldData.total + (hadExistingEntry ? 0 : 1)
                    }
                })
            } catch (error) {
                console.error('Error processing notification:', error)
            }
        })

        return () => {
            socket.emit('leave', `users:${userId}`)
            socket.off('pong')
            socket.off('notification')
            clearInterval(intervalId)
        }
    }, [userId, queryClient])
}
